import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { InputField } from "components/designV2/inputfield/inputfield";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Tag } from "components/designV2/tag/tag";
import { ColorV2 } from "config/colors";
import { RecruitFormData } from "../modal/gigFormModal.types";
import { CompanyGigSkills } from "model/Gig";
import {
  SkillsAddButtonWrap,
  SkillsInputFieldWrap,
  SkillsInputRow,
  SkillsSection,
  SkillsTags,
  transparentSecondaryButtonStyles,
} from "./gigSkills.styled";

type Props = {
  setFormData: React.Dispatch<React.SetStateAction<RecruitFormData>>;
  formData: RecruitFormData;
};

export const GigSkills: React.FC<Props> = (props) => {
  const MAX_SKILL_LENGTH = 24;
  const { t } = useTranslation();
  const [skillText, setSkillText] = useState("");
  const normalizedInput = skillText.trim();

  const normalizeSkillForCompare = (value: string): string =>
    value.trim().toLocaleLowerCase();

  const removeSkill = (skill: CompanyGigSkills) => {
    props.setFormData((prev) => {
      const hasDeleteEntry = "deleteEntry" in skill;

      if (hasDeleteEntry) {
        const newSkills = prev.skills.map((el) =>
          el === skill ? { ...el, deleteEntry: true } : el,
        );

        return { ...prev, skills: newSkills };
      }

      const newSkills = prev.skills.filter((el) => el !== skill);
      return { ...prev, skills: newSkills };
    });
  };

  const addSkill = (value: string) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) return;
    const normalizedCompareValue = normalizeSkillForCompare(normalizedValue);

    props.setFormData((prev) => {
      const duplicate = prev.skills.find(
        (el) => normalizeSkillForCompare(el.skill) === normalizedCompareValue,
      );

      if (duplicate && "deleteEntry" in duplicate && duplicate.deleteEntry) {
        const newSkills = prev.skills.map((el) =>
          normalizeSkillForCompare(el.skill) === normalizedCompareValue
            ? { ...el, deleteEntry: false }
            : el,
        );

        return { ...prev, skills: newSkills };
      }

      if (!duplicate) {
        const newSkills: CompanyGigSkills[] = [
          ...prev.skills,
          { skill: normalizedValue, experience: 0 },
        ];

        return { ...prev, skills: newSkills };
      }

      return prev;
    });

    setSkillText("");
  };

  const visibleSkills = props.formData.skills.filter((skill) => {
    return !("deleteEntry" in skill) || !skill.deleteEntry;
  });

  return (
    <SkillsSection>
      <SkillsInputRow>
        <SkillsInputFieldWrap>
          <InputField
            label={t("CreateShift.ExperienceSkills")}
            value={skillText}
            onChange={(val) => setSkillText(val)}
            onClear={() => setSkillText("")}
            placeholder={t("CreateShift.ExperienceSkillsPlaceholder")}
            maxLength={MAX_SKILL_LENGTH}
            fullWidth
          />
        </SkillsInputFieldWrap>
        <SkillsAddButtonWrap>
          <SecondaryButton
            label={t("General.Add")}
            size="small"
            disabled={
              !normalizedInput || normalizedInput.length > MAX_SKILL_LENGTH
            }
            onClick={() => addSkill(skillText)}
            styleOverrides={transparentSecondaryButtonStyles}
          />
        </SkillsAddButtonWrap>
      </SkillsInputRow>

      {visibleSkills.length > 0 && (
        <SkillsTags>
          {visibleSkills.map((skill) => (
            <Tag
              key={skill.id != null ? String(skill.id) : skill.skill}
              text={t(skill.skill, { ns: "skills" })}
              backgroundColor={ColorV2.Blue6}
              textColor={ColorV2.Blue4}
              onClear={() => removeSkill(skill)}
              maxWidth={180}
            />
          ))}
        </SkillsTags>
      )}
    </SkillsSection>
  );
};
